import React from 'react';
import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import {injectIntl} from 'react-intl';
import {
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';

import Loader from '../dashboard/Loader';
import ErrorModal from '../ui/ErrorModal';
import API from '../../common/utils/API';
import {DateTimeCell} from './formatDateTime';
import {labelFor} from './factLabels';

const TABLE_SCROLL_HEIGHT = 320;

const BAND_COLORS = {
    featured: '#00BB04',
    cooling: '#FFBB28',
    candidate: '#28BBFF',
    none: '#cccccc',
};

/**
 * Filas de la gráfica por nivel. `label` es el nombre traducido que muestran
 * la leyenda y el tooltip; `band` conserva el valor crudo para el color.
 */
export function toBandRows(bands, intl) {
    return Object.keys(bands).map((band) => ({
        band,
        label: intl ? labelFor(intl, 'BAND', band) : band,
        value: bands[band],
    }));
}

export function toHistogramRows(histogram) {
    return histogram.map((bucket) => ({
        name: `${bucket.from === null ? '-inf' : bucket.from} a ${
            bucket.to === null ? '+inf' : bucket.to
        }`,
        count: bucket.count,
    }));
}

export class DistributionCharts extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            error: false,
            bands: {featured: 0, cooling: 0, candidate: 0, none: 0},
            histogram: [],
            baseline: [],
        };

        this.fetchData = this.fetchData.bind(this);
    }

    async componentDidMount() {
        await this.fetchData();
    }

    async fetchData() {
        this.setState({loading: true});

        try {
            const response = await API.reports.getRelevanceDistribution();

            this.setState({
                bands: response.bands,
                histogram: response.histogram,
                baseline: response.baseline,
                loading: false,
                error: false,
            });
        } catch (e) {
            this.setState({loading: false, error: true});
        }
    }

    render() {
        const intl = this.props.intl;
        const {bands, histogram, baseline, loading, error} = this.state;
        const bandRows = toBandRows(bands, intl);
        const isEmpty = bandRows.every((row) => row.value === 0);

        return (
            <div
                data-test='distributionChartsComponent'
                style={{position: 'relative'}}
            >
                <Loader loading={loading} />
                <div className='d-flex justify-content-end mb-2'>
                    <Button
                        variant='secondary'
                        size='sm'
                        onClick={this.fetchData}
                        data-test='distributionRefreshButton'
                    >
                        {intl.formatMessage({id: 'RELEVANCE.REFRESH_BUTTON'})}
                    </Button>
                </div>
                {isEmpty && (
                    <p data-test='distributionEmptyState'>
                        {intl.formatMessage({
                            id: 'RELEVANCE.DISTRIBUTION_EMPTY',
                        })}
                    </p>
                )}
                <Row>
                    <Col md={6}>
                        <Card className='mb-4'>
                            <Card.Header>
                                {intl.formatMessage({
                                    id:
                                        'RELEVANCE.DISTRIBUTION_BAND_CHART_TITLE',
                                })}
                            </Card.Header>
                            <Card.Body
                                style={{
                                    width: '100%',
                                    height: 300,
                                    position: 'relative',
                                }}
                            >
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie
                                            data={bandRows}
                                            dataKey='value'
                                            nameKey='label'
                                            label
                                        >
                                            {bandRows.map((row) => (
                                                <Cell
                                                    key={row.band}
                                                    fill={
                                                        BAND_COLORS[row.band] ||
                                                        '#8884d8'
                                                    }
                                                />
                                            ))}
                                        </Pie>
                                        <Legend />
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={6}>
                        <Card className='mb-4'>
                            <Card.Header>
                                {intl.formatMessage({
                                    id:
                                        'RELEVANCE.DISTRIBUTION_HISTOGRAM_TITLE',
                                })}
                            </Card.Header>
                            <Card.Body
                                style={{
                                    width: '100%',
                                    height: 300,
                                    position: 'relative',
                                }}
                            >
                                <ResponsiveContainer>
                                    <BarChart data={toHistogramRows(histogram)}>
                                        <CartesianGrid strokeDasharray='3 3' />
                                        <XAxis dataKey='name' />
                                        <YAxis allowDecimals={false} />
                                        <Tooltip />
                                        <Bar dataKey='count' fill='#8157fd' />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                <Card className='mb-4'>
                    <Card.Header>
                        {intl.formatMessage({
                            id: 'RELEVANCE.DISTRIBUTION_BASELINE_TABLE_TITLE',
                        })}
                    </Card.Header>
                    <Card.Body>
                        <div
                            data-test='baselineTableScroll'
                            style={{
                                maxHeight: TABLE_SCROLL_HEIGHT,
                                overflowY: 'auto',
                            }}
                        >
                            <Table
                                striped
                                bordered
                                size='sm'
                                data-test='baselineTable'
                            >
                                <thead>
                                    <tr>
                                        <th>
                                            {intl.formatMessage({
                                                id:
                                                    'RELEVANCE.BASELINE_BUCKET_HEADER',
                                            })}
                                        </th>
                                        <th>
                                            {intl.formatMessage({
                                                id:
                                                    'RELEVANCE.MEAN_RATE_HEADER',
                                            })}
                                        </th>
                                        <th>
                                            {intl.formatMessage({
                                                id: 'RELEVANCE.STD_DEV_HEADER',
                                            })}
                                        </th>
                                        <th>
                                            {intl.formatMessage({
                                                id:
                                                    'RELEVANCE.SAMPLE_SIZE_HEADER',
                                            })}
                                        </th>
                                        <th>
                                            {intl.formatMessage({
                                                id:
                                                    'RELEVANCE.COMPUTED_AT_HEADER',
                                            })}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {baseline.map((bucket, index) => (
                                        <tr
                                            key={`${bucket.bucketKey}-${index}`}
                                            data-test='baselineRow'
                                        >
                                            <td>{bucket.bucketKey}</td>
                                            <td>{bucket.meanRate}</td>
                                            <td>{bucket.stdDev}</td>
                                            <td>{bucket.sampleSize}</td>
                                            <td>
                                                <DateTimeCell
                                                    iso={bucket.computedAt}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
                <ErrorModal
                    isLoadErrorModalOpen={error}
                    toggleLoadErrorModal={() => this.setState({error: false})}
                    errorMessage={intl.formatMessage({
                        id: 'RELEVANCE.LOAD_ERROR',
                    })}
                    handleRetryClick={this.fetchData}
                    returnUrl='/relevance'
                    loading={loading}
                />
            </div>
        );
    }
}

export default injectIntl(DistributionCharts);
